import { BRAND, HEADER_CSS, esc, page, traceLine } from './base.js'
import { CONSENT_DOCS, resolveConsentSlug } from '../../data/consentDocs.js'

/**
 * หนังสือแสดงความยินยอม / Informed Consent: the signed PDF filed on the chart
 * when a patient accepts on the CRM's เปิดฟอร์ม page (src/pages/ConsentForm.jsx).
 *
 * Rendered server-side only, by functions/api/consents/[token].js at accept
 * time, then attached to chart.document[] exactly like the Patient Portal's
 * signed consent (doc_type ใบยินยอม, created_at === consent.accept_at).
 *
 * The document text is CONSENT_DOCS (copied from the Patient Portal), so what
 * the patient read on screen is what gets printed. Types without a document
 * (บัตรทอง extra, กำหนดเอง) print CONSENT_GENERIC, the same fallback the form shows.
 *
 * Payload:
 *   { lang, consent_type, consent_label, note, patient_fullname, HN,
 *     branch_name, branch_address, treatment_label, signature (PNG data: URI),
 *     signed_at, consent_id, treatment_ids, printed_at, printed_atEN }
 */

// Generic consent for types with no structured document. Copied from
// patient_portal ConsentSign.jsx so both apps say the same thing.
export const CONSENT_GENERIC = {
  th: {
    intro: (name, hn) =>
      `ข้าพเจ้า ${name}${hn ? ` (HN ${hn})` : ''} ในฐานะผู้รับบริการ ขอแสดงความยินยอมรับการรักษาทางทันตกรรมตามรายละเอียดดังต่อไปนี้`,
    clauses: [
      'ข้าพเจ้าได้รับการอธิบายจากทันตแพทย์เกี่ยวกับการวินิจฉัย แผนการรักษา ทางเลือกในการรักษา ความเสี่ยง และภาวะแทรกซ้อนที่อาจเกิดขึ้น ตลอดจนค่าใช้จ่ายโดยละเอียดแล้ว',
      'ข้าพเจ้าเข้าใจว่าผลของการรักษาขึ้นอยู่กับสภาพร่างกายของข้าพเจ้า และการปฏิบัติตามคำแนะนำของทันตแพทย์อย่างเคร่งครัด',
      'ข้าพเจ้าได้มีโอกาสซักถามข้อสงสัย และได้รับคำตอบเป็นที่พอใจแล้ว ก่อนการลงนามในเอกสารฉบับนี้',
      'ข้าพเจ้ายินยอมให้ทันตแพทย์และทีมผู้ให้การรักษาดำเนินการรักษา ตามแผนการรักษาที่ได้ตกลงร่วมกัน',
      'ข้าพเจ้ายินยอมให้คลินิกเก็บรวบรวม ใช้ และเปิดเผยข้อมูลส่วนบุคคล และข้อมูลด้านสุขภาพของข้าพเจ้า เพื่อวัตถุประสงค์ในการรักษาพยาบาล ตามพระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคล พ.ศ. 2562',
    ],
  },
  en: {
    intro: (name, hn) =>
      `I, ${name}${hn ? ` (HN ${hn})` : ''}, as the recipient of care, hereby consent to dental treatment as detailed below:`,
    clauses: [
      'The dentist has explained the diagnosis, treatment plan, alternative options, risks, and potential complications, as well as the associated costs, in detail.',
      "I understand that the treatment outcome depends on my physical condition and strict adherence to the dentist's instructions.",
      'I have had the opportunity to ask questions and received satisfactory answers before signing this document.',
      'I consent to the dentist and the care team performing treatment according to the agreed treatment plan.',
      'I consent to the clinic collecting, using, and disclosing my personal and health data for treatment purposes in accordance with the Personal Data Protection Act B.E. 2562 (2019).',
    ],
  },
}

// The acknowledgement the patient ticked on the form. Same wording as the form.
export const CONSENT_AGREE = {
  th: 'ข้าพเจ้าได้อ่านและเข้าใจข้อความข้างต้นโดยตลอดแล้ว และยินยอมรับการรักษาตามเอกสารฉบับนี้',
  en: 'I have read and fully understood the information above, and I consent to the treatment described in this document.',
}

const L = {
  th: {
    title: 'หนังสือแสดงความยินยอม', sub: 'Informed Consent',
    type: 'ประเภท', signedAt: 'วันที่ลงนาม',
    patient: 'ผู้ให้ความยินยอม', hn: 'HN', treatment: 'การรักษา',
    clinic: 'สถานพยาบาล', clinicName: 'คลินิกทันตกรรมสไมล์ซีซันส์ สาขา',
    note: 'หมายเหตุจากทันตแพทย์',
    sigRole: 'ผู้ให้ความยินยอม', sigAt: 'ลงนามอิเล็กทรอนิกส์เมื่อ',
    ref: 'รหัสใบยินยอม',
    footer: 'คลินิกทันตกรรมสไมล์ซีซันส์ สาขา',
  },
  en: {
    title: 'Informed Consent', sub: 'หนังสือแสดงความยินยอม',
    type: 'Consent type', signedAt: 'Date signed',
    patient: 'Consenting patient', hn: 'HN', treatment: 'Treatment',
    clinic: 'Healthcare facility', clinicName: 'Smile Seasons Dental Clinic · ',
    note: 'Note from the dentist',
    sigRole: 'Consenting patient', sigAt: 'Signed electronically on',
    ref: 'Consent ID',
    footer: 'Smile Seasons Dental Clinic · ',
  },
}

// Only a PNG data: URI from the signature pad is printed — anything else is dropped.
const SIGNATURE_RE = /^data:image\/png;base64,[A-Za-z0-9+/=]+$/

export function renderConsent(d, { lang = 'th', assets = {} } = {}) {
  const t = L[lang] || L.th
  const slug = resolveConsentSlug(d.consent_type)
  const doc = slug ? CONSENT_DOCS[slug]?.[lang] || CONSENT_DOCS[slug]?.th : null
  const generic = CONSENT_GENERIC[lang] || CONSENT_GENERIC.th
  const signature = SIGNATURE_RE.test(String(d.signature || '')) ? d.signature : ''

  const item = (it) => {
    if (typeof it === 'string') return `<li>${esc(it)}</li>`
    const sub = Array.isArray(it.sub) && it.sub.length
      ? `<ul class="sub">${it.sub.map((s) => `<li>${esc(s)}</li>`).join('')}</ul>`
      : ''
    return `<li>${it.b ? `<b>${esc(it.b)}</b> ` : ''}${esc(it.text || '')}${sub}</li>`
  }

  const content = doc
    ? `<h2 class="doc-title">${esc(doc.title)}</h2>
       ${doc.sections.map((s) => `
       <section class="sec">
         <div class="sec-head">${esc(s.summary)}</div>
         ${s.intro ? `<p class="intro">${esc(s.intro)}</p>` : ''}
         <ul class="items">${(s.items || []).map(item).join('')}</ul>
         ${s.note ? `<p class="sec-note">${esc(s.note)}</p>` : ''}
       </section>`).join('')}`
    : `<p class="intro">${esc(generic.intro(d.patient_fullname || '-', d.HN))}</p>
       <ol class="items">${generic.clauses.map((c) => `<li>${esc(c)}</li>`).join('')}</ol>`

  const body = `
  <header class="top">
    <div>
      <h1>${t.title}</h1>
      <div class="sub">${t.sub}</div>
    </div>
    ${assets.logo ? `<img class="logo" src="${esc(assets.logo)}" alt="Smile Seasons Dental Clinic">` : ''}
  </header>

  <section class="meta">
    <div><span>${t.type}</span><b>${esc(d.consent_label || d.consent_type || '-')}</b></div>
    <div><span>${t.signedAt}</span><b>${esc(d.signed_at || '-')}</b></div>
  </section>

  <section class="parties">
    <div class="party">
      <div class="label">${t.patient}</div>
      <div class="name">${esc(d.patient_fullname || '-')}</div>
      <div class="kv"><span>${t.hn}</span><span class="num">${esc(d.HN || '-')}</span></div>
      ${d.treatment_label ? `<div class="kv"><span>${t.treatment}</span>${esc(d.treatment_label)}</div>` : ''}
    </div>
    <div class="party">
      <div class="label">${t.clinic}</div>
      <div class="name">${esc(t.clinicName)}${esc(d.branch_name || '')}</div>
      ${d.branch_address ? `<div class="plain">${esc(d.branch_address)}</div>` : ''}
    </div>
  </section>

  ${String(d.note || '').trim() ? `
  <section class="note">
    <div class="note-label">${t.note}</div>
    <div class="note-body">${esc(String(d.note).trim())}</div>
  </section>` : ''}

  ${content}

  <section class="agree">
    <span class="tick">✓</span>
    <span>${esc(CONSENT_AGREE[lang] || CONSENT_AGREE.th)}</span>
  </section>

  <section class="sign">
    <div class="sign-box">
      <div class="sign-img">${signature ? `<img src="${esc(signature)}" alt="signature">` : ''}</div>
      <b>(${esc(d.patient_fullname || '-')})</b>
      <span>${t.sigRole}</span>
      <span class="sig-at">${t.sigAt} ${esc(d.signed_at || '-')}</span>
    </div>
  </section>

  ${d.consent_id ? `<p class="ref">${t.ref} <span class="num">${esc(d.consent_id)}</span></p>` : ''}`

  return page({
    title: `${t.title} ${d.HN || ''}`.trim(),
    lang,
    assets,
    footer: `${t.footer}${d.branch_name || ''}`,
    meta: traceLine(d, lang),
    body,
    css: CSS,
  })
}

const CSS = `${HEADER_CSS}
.note { margin-bottom: 6mm; padding: 2.4mm 3mm; background: #fffbeb; border-left: 2px solid #d97706; break-inside: avoid; }
.note-label { font-size: 8.5pt; font-weight: 600; color: #b45309; margin-bottom: .6mm; }
.note-body { white-space: pre-wrap; overflow-wrap: anywhere; }

.doc-title { margin: 0 0 4mm; font-family: 'Plex Thai', sans-serif; font-weight: 600; font-size: 13pt; color: ${BRAND.ink}; }
.sec { margin-bottom: 4.5mm; }
.sec-head { padding: 1.8mm 3mm; background: ${BRAND.blueSoft}; border-left: 2px solid ${BRAND.blue}; font-size: 9.5pt; font-weight: 600; color: ${BRAND.blue}; break-after: avoid; }
.intro { margin: 2.4mm 0 1.6mm; font-size: 10pt; line-height: 1.65; }
.items { margin: 2mm 0 0; padding-left: 6mm; font-size: 10pt; line-height: 1.65; }
.items > li { margin-bottom: 1.4mm; }
.items b { font-weight: 600; }
.items .sub { margin: 1mm 0 0; padding-left: 5mm; list-style: circle; color: #334155; font-size: 9.5pt; }
.sec-note { margin: 2mm 0 0; font-size: 9.5pt; color: ${BRAND.blue}; }

.agree { display: flex; gap: 2.5mm; margin: 6mm 0 0; padding: 3mm; border: 1px solid ${BRAND.rule}; font-size: 10pt; break-inside: avoid; }
.agree .tick { font-weight: 600; color: ${BRAND.blue}; }

.sign { display: flex; justify-content: flex-end; margin-top: 8mm; break-inside: avoid; }
.sign-box { width: 72mm; display: flex; flex-direction: column; align-items: center; text-align: center; font-size: 9.5pt; }
.sign-img { width: 100%; height: 22mm; border-bottom: 1px dotted #94a3b8; margin-bottom: 2mm; display: flex; align-items: flex-end; justify-content: center; }
.sign-img img { max-height: 21mm; max-width: 100%; object-fit: contain; }
.sign-box b { font-weight: 600; font-size: 10.5pt; }
.sign-box span { color: ${BRAND.muted}; }
.sign-box .sig-at { font-size: 8.5pt; }

.ref { margin: 6mm 0 0; font-size: 7.5pt; color: #94a3b8; }
`
